/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { Capacitor } from '@capacitor/core'
import { setAccessTokenGetter } from '../api/api'
import { auth, confirmEnrollment as confirmEnrollmentApi } from '../api/auth'
import { postUpdate } from '../api/react/settings/Profile'
import { unregisterFCMDevice } from '../api/react/notifications/devices'
import { FCM_TOKEN_STORAGE_KEY } from '../hooks/useFCMRegistration'
import FCMRuntime from '../components/FCMRuntime'
import {
  Auth0Authenticator,
  AuthOrganization,
  AuthTokenResponse,
  AuthUserData,
  MfaFactorType,
} from '../types/auth.types'
import eventEmitter from '../utils/functions/eventEmitter'

export const MFA_REQUIRED_ERROR = 'mfa_required'

export interface SelectMfaFactorResult {
  requiresEnroll: boolean
  authenticatorId: string | null
}

interface UserContextType {
  permissions: {
    isSuperAdmin: boolean
    isAdmin: boolean
    isReact: boolean
    isMember: boolean
    isMemberPro: boolean
    isReactOnlineEnf: boolean
    isReactPartner: boolean
  }
  members: AuthOrganization[]
  user: AuthUserData | null
  isAuthenticated: boolean
  accessDenied: boolean
  error: boolean
  loading: { query: boolean; mutation: boolean; invalidate: boolean }
  loginWithUsernameAndPassword: (username: string, password: string) => void
  loginWithPassword: (username: string, password: string) => Promise<void>
  selectMfaFactor: (factorType: MfaFactorType, authenticators: Auth0Authenticator[]) => SelectMfaFactorResult | null
  handleTokens: (tokens: AuthTokenResponse) => Promise<void>
  confirmEnrollment: (otp: string, client_secret: string) => Promise<void>
  logout: () => Promise<void>
  logoutNative: () => Promise<void>
}

export const UserContext = createContext<UserContextType | undefined>(undefined)

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const MINIMUM_LOADING_TIME = import.meta.env.DEV ? 0 : 1600

  const {
    isAuthenticated: auth0Ready,
    isLoading: auth0Loading,
    getAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout,
  } = useAuth0()

  const [user, setUser] = useState<AuthUserData | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [accessDenied, setAccessDenied] = useState(false)
  const [customIsLoading, setCustomIsLoading] = useState(true)
  const [nativeLoading, setNativeLoading] = useState(false)
  const loadingStartRef = useRef<number | null>(null)

  const queryClient = useQueryClient()

  // Clear the logout-in-flight flag once we've returned from Auth0
  useEffect(() => {
    sessionStorage.removeItem('logging_out')
  }, [])

  // Native: restore session from cached Auth0 token on app launch
  useEffect(() => {
    console.log('[restore] effect running, isNative:', Capacitor.isNativePlatform())
    if (!Capacitor.isNativePlatform()) return
    const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID
    const audience = import.meta.env.VITE_AUTH0_AUDIENCE
    const cacheKey = `@@auth0spajs@@::${clientId}::${audience}::openid profile email offline_access`
    const raw = localStorage.getItem(cacheKey)
    console.log('[restore] cached token present:', !!raw)
    if (!raw) return

    let cached: any
    try {
      cached = JSON.parse(raw)
    } catch {
      localStorage.removeItem(cacheKey)
      return
    }

    const accessToken: string | undefined = cached?.body?.access_token
    const expiresAt: number | undefined = cached?.expiresAt
    const expired = !accessToken || !expiresAt || expiresAt * 1000 < Date.now()
    console.log('[restore] token expired:', expired, 'expiresAt:', expiresAt)
    if (expired) {
      localStorage.removeItem(cacheKey)
      return
    }

    setAccessTokenGetter(async () => accessToken)
    setNativeLoading(true)
    auth()
      .then((oceanUser) => {
        console.log('[restore] ocean user loaded:', oceanUser?.data?.email)
        setUser(oceanUser.data)
        setIsAuthenticated(true)
        queryClient.setQueryData(['authUser'], oceanUser)
      })
      .catch((e: any) => {
        console.error('[restore] ocean user failed:', e?.response?.status, e?.message)
        localStorage.removeItem(cacheKey)
      })
      .finally(() => setNativeLoading(false))
  }, [queryClient])

  // Bridge the SDK's token getter into the Axios interceptor.
  // Also writes id_token/is_authenticated to localStorage for the Ocean browser plugin.
  useEffect(() => {
    if (auth0Ready) {
      const tokenGetter = async () => {
        const token = await getAccessTokenSilently()
        localStorage.setItem('id_token', token)
        localStorage.setItem('is_authenticated', 'true')
        return token
      }
      setAccessTokenGetter(tokenGetter)
    }
  }, [auth0Ready, getAccessTokenSilently])

  // Fetch ocean user once Auth0 session is ready
  const authUser = useQuery({
    queryKey: ['authUser'],
    queryFn: auth,
    enabled: auth0Ready,
    refetchOnWindowFocus: false,
    retry: 0,
  })

  // Handle auth success
  useEffect(() => {
    if (authUser.data) {
      setUser(authUser.data.data)
      setIsAuthenticated(true)

      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        const userData = authUser.data.data
        postUpdate({
          id: userData.id ?? '',
          email: userData.email ?? '',
          first_name: userData.first_name ?? '',
          last_name: userData.last_name ?? '',
          phone: userData.phone ?? '',
          location: userData.location ?? '',
          city: userData.city ?? '',
          country: userData.country ?? '',
          timezone: timezone,
        })
      } catch (error) {
        console.warn('Failed to detect timezone:', error)
      }
    }
  }, [authUser.data])

  // Handle auth error
  useEffect(() => {
    if (authUser.isError) {
      const status = (authUser.error as any)?.response?.status
      console.warn('[auth-debug] authUser.isError', { status, error: authUser.error, auth0Ready })
      setUser(null)
      setIsAuthenticated(false)
      localStorage.removeItem('id_token')
      localStorage.removeItem('is_authenticated')
      if (auth0Ready && (status === 403 || status === 401)) {
        setAccessDenied(true)
      }
    }
  }, [authUser.isError, authUser.error, auth0Ready])

  // Trace auth state transitions
  useEffect(() => {
    console.log('[auth-debug] state', {
      auth0Ready,
      auth0Loading,
      authUserIsLoading: authUser.isLoading,
      authUserIsError: authUser.isError,
      isAuthenticated,
      accessDenied,
      url: window.location.href,
    })
  }, [auth0Ready, auth0Loading, authUser.isLoading, authUser.isError, isAuthenticated, accessDenied])

  const getReturnToPath = () => {
    const currentHashRoute = globalThis.location.hash.replace(/^#/, '')
    return currentHashRoute || '/react/dashboard'
  }

  const loginWithUsernameAndPassword = (_username = '', _password = '', returnTo = getReturnToPath()) => {
    console.warn('[auth-debug] loginWithUsernameAndPassword called', { returnTo, url: window.location.href, stack: new Error().stack })
    return loginWithRedirect({ appState: { returnTo } })
  }

  // Shared post-token-exchange logic — cache tokens, hook up axios, fetch ocean user.
  // Used by both initial password login and MFA confirm/enroll completion.
  const handleTokens = useCallback(async (tokens: AuthTokenResponse) => {
    const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID
    const audience = import.meta.env.VITE_AUTH0_AUDIENCE

    localStorage.setItem(
      `@@auth0spajs@@::${clientId}::${audience}::openid profile email offline_access`,
      JSON.stringify({
        body: {
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
          id_token: tokens.id_token,
          token_type: 'Bearer',
          expires_in: tokens.expires_in,
          scope: 'openid profile email offline_access',
        },
        expiresAt: Math.floor(Date.now() / 1000) + tokens.expires_in,
      })
    )
    console.log('[login] tokens cached in localStorage')

    setAccessTokenGetter(async () => tokens.access_token)

    setNativeLoading(true)
    try {
      console.log('[login] fetching ocean user from backend')
      const oceanUser = await auth()
      console.log('[login] ocean user OK', { email: oceanUser?.data?.email, userType: oceanUser?.data?.user_type })
      setUser(oceanUser.data)
      setIsAuthenticated(true)
      queryClient.setQueryData(['authUser'], oceanUser)
    } catch (e: any) {
      console.error('[login] ocean user fetch failed', {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
        url: e?.config?.url,
      })
      throw new Error('Login failed')
    } finally {
      setNativeLoading(false)
    }
    console.log('[login] done — isAuthenticated=true')
  }, [queryClient])

  // Mobile-only: Resource Owner Password Grant — calls Auth0 token endpoint directly
  const loginWithPassword = useCallback(async (username: string, password: string) => {
    const domain = import.meta.env.VITE_AUTH0_DOMAIN
    const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID
    const audience = import.meta.env.VITE_AUTH0_AUDIENCE
    const apiEndpoint = import.meta.env.VITE_API_ENDPOINT

    console.log('[login] start', {
      username,
      domain,
      clientId,
      audience,
      apiEndpoint,
      mode: import.meta.env.MODE,
      isNative: Capacitor.isNativePlatform(),
      origin: globalThis.location.origin,
    })

    setNativeLoading(true)
    let tokens: AuthTokenResponse
    try {
      console.log('[login] POST', `https://${domain}/oauth/token`)
      const res = await axios.post<AuthTokenResponse>(`https://${domain}/oauth/token`, {
        grant_type: 'password',
        username,
        password,
        audience,
        scope: 'openid profile email offline_access',
        client_id: clientId,
        connection: 'Username-Password-Authentication',
      })
      tokens = res.data
      console.log('[login] auth0 token response OK', {
        hasAccessToken: !!tokens.access_token,
        hasRefreshToken: !!tokens.refresh_token,
        hasIdToken: !!tokens.id_token,
        expiresIn: tokens.expires_in,
      })
    } catch (e: any) {
      const status = e?.response?.status
      const data = e?.response?.data
      console.error('[login] auth0 token request failed', {
        status,
        error: data?.error,
        error_description: data?.error_description,
        message: e?.message,
        url: e?.config?.url,
      })
      setNativeLoading(false)

      // MFA required — stash the mfa_token, let Login.tsx redirect to /auth/mfa-select
      if (data?.error === 'mfa_required' && data?.mfa_token) {
        console.log('[login] mfa_required — storing mfa_token and signalling MFA flow')
        localStorage.setItem('mfa_token', data.mfa_token)
        const err: any = new Error('MFA required')
        err.code = MFA_REQUIRED_ERROR
        throw err
      }

      throw new Error(data?.error_description ?? 'Login failed')
    }

    setNativeLoading(false)
    await handleTokens(tokens)
  }, [handleTokens])

  // Pick which Auth0 authenticator matches the chosen factor, or signal enrollment is needed.
  // Stores the resolved IDs in localStorage so OtpChallenge can read them after navigation.
  const selectMfaFactor = useCallback(
    (factorType: MfaFactorType, authenticators: Auth0Authenticator[]): SelectMfaFactorResult | null => {
      const matcher = (a: Auth0Authenticator) =>
        factorType === 'otp'
          ? a.authenticator_type === 'otp' && a.active
          : a.authenticator_type === 'oob' && a.oob_channel === 'email' && a.active

      const match = authenticators.find(matcher)
      localStorage.setItem('mfa_factor_type', factorType)

      if (!match) {
        localStorage.removeItem('mfa_authenticator_id')
        return { requiresEnroll: true, authenticatorId: null }
      }

      localStorage.setItem('mfa_authenticator_id', match.id)
      return { requiresEnroll: false, authenticatorId: match.id }
    },
    [],
  )

  // Called from OtpEnroll after the user enters the code from their authenticator app.
  const confirmEnrollment = useCallback(
    async (otp: string, client_secret: string) => {
      const mfa_token = localStorage.getItem('mfa_token')
      if (!mfa_token) throw new Error('Missing mfa_token')

      console.log('[login] confirming enrollment with mfa-otp grant')
      const tokens = await confirmEnrollmentApi({ mfa_token, otp, client_secret })
      await handleTokens(tokens)
      localStorage.removeItem('mfa_token')
      localStorage.removeItem('mfa_factor_type')
      localStorage.removeItem('mfa_authenticator_id')
    },
    [handleTokens],
  )

  const unregisterPushDeviceIfAny = useCallback(async () => {
    const token = localStorage.getItem(FCM_TOKEN_STORAGE_KEY)
    if (!token) return
    localStorage.removeItem(FCM_TOKEN_STORAGE_KEY)
    try {
      await unregisterFCMDevice(token)
    } catch {
      // Backend cleanup task will prune the row eventually if this fails.
    }
  }, [])

  const logout = useCallback(async () => {
    console.warn('[auth-debug] logout called', { url: window.location.href, stack: new Error().stack })
    if (sessionStorage.getItem('logging_out')) return
    sessionStorage.setItem('logging_out', '1')
    await unregisterPushDeviceIfAny()
    setUser(null)
    setIsAuthenticated(false)
    localStorage.removeItem('id_token')
    localStorage.removeItem('is_authenticated')
    queryClient.clear()
    auth0Logout({ logoutParams: { returnTo: globalThis.location.origin } })
  }, [queryClient, auth0Logout, unregisterPushDeviceIfAny])

  // Mobile-only: clear tokens + state locally, no Auth0 redirect
  const logoutNative = useCallback(async () => {
    const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID
    const audience = import.meta.env.VITE_AUTH0_AUDIENCE
    await unregisterPushDeviceIfAny()
    localStorage.removeItem(
      `@@auth0spajs@@::${clientId}::${audience}::openid profile email offline_access`
    )
    localStorage.removeItem('mfa_token')
    localStorage.removeItem('mfa_factor_type')
    localStorage.removeItem('mfa_authenticator_id')
    setAccessTokenGetter(async () => '')
    setUser(null)
    setIsAuthenticated(false)
    queryClient.clear()
    globalThis.location.hash = '/auth/login'
  }, [queryClient, unregisterPushDeviceIfAny])

  // Minimum loading time UX
  useEffect(() => {
    const isLoading = auth0Loading || authUser.isLoading || authUser.isFetching
    if (isLoading) {
      if (!loadingStartRef.current) {
        loadingStartRef.current = new Date().getTime()
      }
      setCustomIsLoading(true)
    } else {
      const elapsed = loadingStartRef.current
        ? new Date().getTime() - loadingStartRef.current
        : MINIMUM_LOADING_TIME
      loadingStartRef.current = null

      const remainingTime = MINIMUM_LOADING_TIME - elapsed
      if (remainingTime > 0) {
        const timer = setTimeout(() => setCustomIsLoading(false), remainingTime)
        return () => clearTimeout(timer)
      } else {
        setCustomIsLoading(false)
      }
    }
  }, [auth0Loading, authUser.isLoading, authUser.isFetching, MINIMUM_LOADING_TIME])

  // Event emitter for logout — native uses local-only logout, web uses Auth0 redirect
  useEffect(() => {
    const handler = Capacitor.isNativePlatform() ? logoutNative : logout
    eventEmitter.on('logout', handler)
    return () => {
      eventEmitter.off('logout', handler)
    }
  }, [logout, logoutNative])

  const permissions = {
    isSuperAdmin: user?.user_type === 'super_admin',
    isAdmin: user?.user_type === 'super_admin' || user?.user_type === 'react_admin',
    isReact:
      user?.user_type === 'super_admin' ||
      user?.user_type === 'react_admin' ||
      user?.user_type === 'react_online_enforcement' ||
      user?.user_type === 'react_partner_user',
    isMember: user?.user_type === 'member_user',
    isMemberPro: user?.user_type === 'member_user' && user?.member_plan === 1,
    isReactOnlineEnf: user?.user_type === 'react_online_enforcement',
    isReactPartner: user?.user_type === 'react_partner_user',
  }

  const value: UserContextType = {
    permissions,
    members: authUser.data?.data?.organizations ?? [],
    error: false,
    accessDenied,
    loading: {
      mutation: nativeLoading,
      query: nativeLoading || auth0Loading || authUser.isLoading || customIsLoading,
      invalidate: authUser.isFetching,
    },
    user,
    isAuthenticated,
    loginWithUsernameAndPassword,
    loginWithPassword,
    selectMfaFactor,
    handleTokens,
    confirmEnrollment,
    logout,
    logoutNative,
  }

  return (
    <UserContext.Provider value={value}>
      {Capacitor.isNativePlatform() && <FCMRuntime />}
      {children}
    </UserContext.Provider>
  )
}
